import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X, Plane, Sparkles, Utensils, Headphones, Youtube, BookOpen, ArrowRight, Play,
} from "lucide-react";
import PodcastPlayer from "./PodcastPlayer";
import EnquiryForm from "./EnquiryForm";

const TABS = [
  { id: "arrival", label: "Arrival", icon: Plane },
  { id: "culture", label: "Culture", icon: Sparkles },
  { id: "food", label: "Food", icon: Utensils },
  { id: "listen", label: "Listen", icon: Headphones },
  { id: "films", label: "Films", icon: Youtube },
  { id: "journal", label: "Journal", icon: BookOpen },
];

function Strip({ images = [] }) {
  return (
    <div className="grid grid-cols-3 gap-2 mt-3">
      {images.slice(0, 3).map((src, i) => (
        <div key={i} className="aspect-[3/4] overflow-hidden rounded-md">
          <img src={src} alt={`view ${i + 1}`} loading="lazy" className="w-full h-full object-cover" />
        </div>
      ))}
    </div>
  );
}

export default function ExperienceModal({ exp, tour, open, onOpenChange, autoPodcast }) {
  const [tab, setTab] = useState("arrival");
  const [videoId, setVideoId] = useState(null);
  const [started, setStarted] = useState(false);

  // Each tab opens its own topic video; Listen shows the podcast stage.
  useEffect(() => {
    if (!open || !exp) return;
    setStarted(false);
    const tv = exp.tab_videos || {};
    if (tab === "arrival") setVideoId(tv.arrival || exp.hero_video);
    else if (tab === "culture") setVideoId(tv.culture || exp.hero_video);
    else if (tab === "food") setVideoId(tv.food || exp.hero_video);
    else if (tab === "films") setVideoId(exp.hero_video);
    // journal/listen render their own custom stage (no video)
  }, [tab, open, exp]);

  useEffect(() => { if (open) setTab("arrival"); }, [open]);

  if (!exp) return null;
  const films = exp.youtube || [];
  const blogs = exp.related_blogs || [];

  // Plain embed (same reliable config as the homepage films). Muted-autoplay is intentionally
  // NOT forced — YouTube blocks autoplay embeds in many contexts ("Video unavailable"), so we load
  // the film front-and-centre, ready to play with one tap.
  const src = videoId
    ? `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&playsinline=1&rel=0`
    : null;

  const close = () => onOpenChange(false);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[1100] bg-ink/95 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          data-testid="experience-modal" role="dialog" aria-modal="true"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.97, y: 16 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.35 }}
            className="relative w-full max-w-6xl max-h-[92vh] overflow-hidden rounded-lg bg-ink text-white border border-white/10 flex flex-col"
          >
            {/* header */}
            <div className="flex items-start justify-between gap-4 px-5 sm:px-8 pt-5 pb-3 shrink-0">
              <div className="min-w-0">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-terracotta">The experience</p>
                <h2 className="font-display font-extrabold text-xl sm:text-2xl tracking-tight leading-tight truncate">{tour.name}</h2>
                <p className="text-sm text-white/60 truncate">{tour.subtitle}</p>
              </div>
              <button onClick={close} data-testid="experience-modal-close"
                className="w-9 h-9 shrink-0 flex items-center justify-center rounded-sm bg-white/10 hover:bg-white/20 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto thin-scroll px-5 sm:px-8 pb-5">
              {/* video stage — swaps per tab; Listen shows the podcast */}
              <div className="relative aspect-video w-full rounded-md overflow-hidden bg-black" data-testid="experience-stage">
                {tab === "listen" ? (
                  <div className="relative w-full h-full" data-testid="experience-stage-podcast">
                    <img src={tour.hero_image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center px-6">
                      <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-terracotta">What to expect · Raj &amp; Simone</p>
                      <h3 className="font-display font-extrabold text-xl sm:text-2xl tracking-tight">Listen to the podcast</h3>
                      <PodcastPlayer slug={tour.slug} tourName={tour.name} defaultOpen={autoPodcast} triggerLabel="Play the episode"
                        triggerClassName="flex items-center justify-center gap-2 bg-terracotta hover:bg-terracotta-hover text-white h-12 px-7 rounded-sm font-semibold text-sm tracking-wide transition-colors" />
                    </div>
                  </div>
                ) : tab === "journal" ? (
                  <div className="relative w-full h-full" data-testid="experience-stage-journal">
                    <img src={tour.hero_image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
                    <div className="absolute inset-0 bg-gradient-to-b from-ink/70 to-ink/90" />
                    <div className="relative h-full overflow-y-auto thin-scroll p-6 sm:p-8">
                      <div className="flex items-center gap-2 text-terracotta mb-1">
                        <BookOpen className="w-4 h-4" />
                        <span className="font-mono text-[11px] uppercase tracking-[0.25em]">Read before you go</span>
                      </div>
                      <h3 className="font-display font-extrabold text-xl sm:text-2xl tracking-tight mb-4">From the journal</h3>
                      {blogs.length ? (
                        <div className="grid sm:grid-cols-2 gap-3">
                          {blogs.map((b) => (
                            <a key={b.url} href={b.url} target="_blank" rel="noreferrer" data-testid="experience-read-card"
                              className="group flex flex-col justify-between gap-3 bg-white/8 hover:bg-white/15 border border-white/15 hover:border-terracotta rounded-md p-4 transition-colors">
                              <span className="text-[15px] leading-snug text-white font-medium">{b.title}</span>
                              <span className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest text-terracotta">Read article →</span>
                            </a>
                          ))}
                        </div>
                      ) : (
                        <p className="text-white/60 text-sm">Reading picks are being curated for this journey.</p>
                      )}
                    </div>
                  </div>
                ) : src ? (
                  <div className="relative w-full h-full">
                    <iframe key={videoId} className="w-full h-full" src={src} title={tour.name}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen frameBorder="0" />
                    <a href={`https://www.youtube.com/watch?v=${videoId}`} target="_blank" rel="noreferrer" data-testid="stage-yt-fallback"
                      className="absolute bottom-2 right-2 z-10 text-[11px] font-mono uppercase tracking-wider text-white/85 hover:text-white bg-ink/70 hover:bg-ink px-2.5 py-1 rounded-sm transition-colors">
                      Watch on YouTube ↗
                    </a>
                    <span className="absolute bottom-2 left-2 z-10 text-[11px] font-mono uppercase tracking-wider text-white/85 bg-ink/70 px-2.5 py-1 rounded-sm pointer-events-none">
                      Playing muted — tap 🔊 in the player for sound
                    </span>
                  </div>
                ) : (
                  <img src={tour.hero_image} alt={tour.name} className="w-full h-full object-cover" />
                )}
              </div>

              {/* selectable experiences */}
              <div className="flex flex-wrap gap-2 mt-4" data-testid="experience-tabs">
                {TABS.map((t) => {
                  const Icon = t.icon;
                  const active = tab === t.id;
                  return (
                    <button key={t.id} data-testid={`experience-tab-${t.id}`} onClick={() => setTab(t.id)}
                      className={`flex items-center gap-1.5 px-3.5 h-9 rounded-full text-sm font-medium transition-colors ${active ? "bg-terracotta text-white" : "bg-white/10 text-white/80 hover:bg-white/20"}`}>
                      <Icon className="w-4 h-4" /> {t.label}
                    </button>
                  );
                })}
              </div>

              {/* content panel */}
              <div className="mt-4 min-h-[120px]" data-testid={`experience-panel-${tab}`}>
                {tab === "arrival" && (
                  <div>
                    <h3 className="font-display font-bold text-lg tracking-tight">Touchdown in {exp.arrival.hub}</h3>
                    <p className="font-mono text-[11px] text-white/50 mt-0.5">{exp.arrival.airport}</p>
                    <p className="text-sm text-white/80 leading-relaxed mt-2 max-w-3xl">{exp.arrival.text}</p>
                  </div>
                )}
                {tab === "culture" && (
                  <div>
                    <h3 className="font-display font-bold text-lg tracking-tight">Culture &amp; people</h3>
                    <p className="text-sm text-white/80 leading-relaxed mt-2 max-w-3xl">{exp.culture.text}</p>
                    <Strip images={exp.culture.images} />
                  </div>
                )}
                {tab === "food" && (
                  <div>
                    <h3 className="font-display font-bold text-lg tracking-tight">Eat well, travel strong</h3>
                    <p className="text-sm text-white/80 leading-relaxed mt-2 max-w-3xl">{exp.food.text}</p>
                    <Strip images={exp.food.images} />
                  </div>
                )}
                {tab === "listen" && (
                  <div>
                    <h3 className="font-display font-bold text-lg tracking-tight">Hear what to expect</h3>
                    <p className="text-sm text-white/80 leading-relaxed mt-2 max-w-2xl">A warm 2–3 minute conversation between founder Raj and traveller Simone, drawn from our own journal — press play above to listen.</p>
                  </div>
                )}
                {tab === "films" && (
                  <div>
                    <h3 className="font-display font-bold text-lg tracking-tight">Watch the journey</h3>
                    {films.length ? (
                      <div className="grid sm:grid-cols-2 gap-2 mt-3">
                        {films.map((v) => (
                          <button key={v.id} data-testid={`experience-film-${v.id}`} onClick={() => { setVideoId(v.id); setStarted(true); }}
                            className={`group flex items-center gap-3 p-2 rounded-md text-left transition-colors ${videoId === v.id ? "bg-terracotta/20 border border-terracotta" : "bg-white/5 hover:bg-white/10 border border-white/10"}`}>
                            <div className="relative w-24 aspect-video rounded-sm overflow-hidden bg-black shrink-0">
                              <img src={`https://i.ytimg.com/vi/${v.id}/hqdefault.jpg`} alt="" className="w-full h-full object-cover" />
                              <span className="absolute inset-0 flex items-center justify-center"><Play className="w-5 h-5 text-white drop-shadow" /></span>
                            </div>
                            <span className="text-sm text-white/85 leading-snug">{v.title}</span>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-white/60 mt-2">The film above sets the scene for this journey.</p>
                    )}
                  </div>
                )}
                {tab === "journal" && (
                  <p className="text-sm text-white/70 leading-relaxed max-w-2xl">Hand-picked stories and guides from our journal — tap any card above to read before you go.</p>
                )}
              </div>
            </div>

            {/* footer CTAs */}
            <div className="flex flex-wrap items-center gap-3 px-5 sm:px-8 py-4 border-t border-white/10 shrink-0 bg-ink">
              <EnquiryForm destination={tour.name} tourSlug={tour.slug} triggerLabel="Enquire & reserve your place"
                triggerClassName="flex items-center justify-center gap-2 bg-terracotta hover:bg-terracotta-hover text-white h-11 px-6 rounded-sm font-semibold text-sm tracking-wide transition-colors" />
              <button onClick={close} data-testid="experience-explore-itinerary"
                className="flex items-center justify-center gap-2 border border-white/25 hover:border-white/50 text-white h-11 px-6 rounded-sm font-medium text-sm transition-colors">
                Explore the full itinerary <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
